import React, { useCallback, useRef } from 'react';

import useTranslation from 'hooks/use-translation';
import { ModalExitIcon } from 'components/core';
import { ModalButton, ModalCopy, ModalSecondaryHeader } from 'shared/modals';

export type StoreRoutingOfferData = {
  originStoreName: string;
  destinationStoreName: string;
  address: string;
  willClearCart: boolean;
};

type StoreRoutingOfferProps = {
  data: StoreRoutingOfferData;
  onAccept: () => void;
  onDecline: () => void;
};

export function StoreRoutingOffer({ data, onAccept, onDecline }: StoreRoutingOfferProps): JSX.Element {
  const { t } = useTranslation();
  const acceptedRef = useRef(false);
  const { originStoreName, destinationStoreName, address, willClearCart } = data;
  const heading = t('storeRoutingOffer.heading', "You're outside the delivery area");
  const body = willClearCart
    ? t(
        'storeRoutingOffer.descriptionWithCart',
        "{{ originStoreName }} doesn't deliver to {{ address }}, but {{ destinationStoreName }} does. Switching stores will clear your cart.",
        { originStoreName, destinationStoreName, address }
      )
    : t(
        'storeRoutingOffer.description',
        "{{ originStoreName }} doesn't deliver to {{ address }}, but {{ destinationStoreName }} does.",
        { originStoreName, destinationStoreName, address }
      );
  const primaryLabel = willClearCart
    ? t('storeRoutingOffer.clearAndViewNewLocation', 'Clear cart and view new location')
    : t('storeRoutingOffer.viewNewLocation', 'View new location');

  const handleAccept = useCallback((): void => {
    if (acceptedRef.current) {
      return;
    }
    acceptedRef.current = true;
    onAccept();
  }, [onAccept]);

  return (
    <div role='dialog' aria-modal='true' aria-labelledby='store-routing-offer-title' data-testid='store-routing-offer'>
      <ModalExitIcon
        aria-label='Close store switch offer'
        data-testid='store-routing-offer-close'
        onClick={onDecline}
      />
      <ModalSecondaryHeader
        id='store-routing-offer-title'
        data-testid='store-routing-offer-heading'
        lowercase
        mb='18px'
      >
        {heading}
      </ModalSecondaryHeader>
      <ModalCopy maxWidth={390}>{body}</ModalCopy>
      <ModalButton data-testid='store-routing-offer-primary' onClick={handleAccept}>
        {primaryLabel}
      </ModalButton>
      <ModalButton data-testid='store-routing-offer-stay' inverted onClick={onDecline}>
        {t('storeRoutingOffer.stay', 'Stay on this menu')}
      </ModalButton>
    </div>
  );
}
